import { isNil } from 'lodash';
import { useCallback } from 'react';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { MultiSelect } from '@shared/components/multiSelect';
import { TitledWorkflowTemplate, getEntityTitle } from '@shared/data/entity';
import {
  RelatedEntity,
  RelatedEntityField,
  RelatedItem,
} from '@shared/data/entity/fields/RelatedEntityField';
import { FieldRecord } from '@shared/scenes/entity/types';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useIsDesktop } from '@shared/util/hooks';
import { NoCards } from './NoCards';
import { QuickCreateCard } from './QuickCreateCard';
import { SearchEmpty } from './SearchEmpty';
import { useCreateRelatedEntity } from './useCreateRelatedEntity';
import { useRelatedEntitySearch } from './useRelatedEntitySearch';
import { useWorkflowSupportsQuickCreate } from './useWorkflowSupportsQuickCreate';

interface Props {
  entities: RelatedEntity[];
  onChange(values: RelatedEntity[]): void;
  onClose(): void;
  anchorName: string;
  component: API.RelatedCardComponent;
  field: RelatedEntityField | null;
  fields: FieldRecord;
  canCreateNew: boolean;
}

const EMPTY_ARRAY: RelatedItem[] = [];

export function RelatedEntityPicker({
  entities,
  field,
  anchorName,
  onChange,
  onClose,
  component,
  fields,
  canCreateNew,
}: Props) {
  const {
    onSearch,
    searchResults,
    loading,
    hasMore,
    loadMore,
    secondaryTextById,
    workflowTemplate,
  } = useRelatedEntitySearch(component, fields);
  const items = field?.items ?? EMPTY_ARRAY;

  const handleChange = useCallback(
    (updatedValues: Array<RelatedItem | RelatedEntity>) => {
      // MultiSelect may pass back a mix of RelatedItem (id, quantity) and RelatedEntity.
      // Ensure we return fully formed RelatedEntity objects by resolving via search results and existing entities.
      const entityById = new Map<string, RelatedEntity>();
      // Prefer existing selected entities, then search results
      [...entities, ...searchResults].forEach(entity => {
        if (!entityById.has(entity.id)) {
          entityById.set(entity.id, entity);
        }
      });
      const resolvedEntities: RelatedEntity[] = updatedValues
        .map(value => {
          // If it's already a RelatedEntity, use it directly
          const maybeEntity = value as RelatedEntity;
          if (!isNil(maybeEntity?.workflowTemplateId)) {
            return maybeEntity;
          }
          // Otherwise resolve by id from our lookup
          const item = value as RelatedItem;
          return entityById.get(item.id);
        })
        .filter((e): e is RelatedEntity => !isNil(e));
      onChange(resolvedEntities);
    },
    [onChange, entities, searchResults],
  );

  const addNewEntity = useCreateRelatedEntity({
    workflowTemplateId: component.workflowTemplateId,
  });

  const handleCreateNewEntity = useCallback(
    async (name: string) => {
      const newEntity = await addNewEntity(name);
      if (!isNil(newEntity)) {
        onChange([...entities, newEntity]);
      }
    },
    [onChange, entities, addNewEntity],
  );

  const workflowSupportsQuickCreate = useWorkflowSupportsQuickCreate({
    workflowTemplateId: component.workflowTemplateId,
  });
  const allowCreation = canCreateNew && workflowSupportsQuickCreate;

  const renderTitleBar = useCallback(() => {
    return (
      <AnchoredSheetTitleBar
        title={component.label ?? ''}
        rightIcon="exit"
        onRightIconPress={onClose}
      />
    );
  }, [onClose, component.label]);

  const renderCreateNew = useCallback(
    (query: string, creating: boolean) => {
      if (!allowCreation) {
        return null;
      }
      if (creating) {
        return <LoadingIndicator padding={4} />;
      }
      return <QuickCreateCard channelName={component.label ?? ''} query={query} />;
    },
    [component.label, allowCreation],
  );

  const match = useCallback(
    (entity: RelatedEntity, query: string, exact: boolean) => {
      const name = getEntityTitle(entity, workflowTemplate) ?? '';
      if (exact) {
        return name === query;
      }
      return true;
    },
    [workflowTemplate],
  );

  const renderEmptyState = useCallback(
    (query: string) => {
      return (
        <View paddingVertical={3} paddingHorizontal={2} center style={{ minHeight: grid(16) }}>
          {query.length > 0 && <SearchEmpty keyword={query} />}
          {query.length === 0 && !loading && <NoCards />}
          {query.length === 0 && loading && <LoadingIndicator />}
        </View>
      );
    },
    [loading],
  );

  const renderItem = useCallback(
    (entity: RelatedEntity) => (
      <EntityPickerItem
        entity={entity}
        workflowTemplate={workflowTemplate}
        secondaryText={secondaryTextById.get(entity.id)}
      />
    ),
    [secondaryTextById, workflowTemplate],
  );

  const isDesktop = useIsDesktop();
  const fixedWidth = isDesktop ? grid(46) : undefined;

  return (
    <AnchoredSheet
      anchorName={anchorName}
      renderTitleBar={!isDesktop ? renderTitleBar : undefined}
      onClose={onClose}
      style={{ width: fixedWidth, minHeight: grid(36) }}
    >
      <MultiSelect<RelatedItem>
        onChange={handleChange}
        keyExtractor={entity => entity.id}
        match={match}
        renderItem={renderItem}
        renderEmptyState={renderEmptyState}
        onCreate={handleCreateNewEntity}
        renderCreateNew={allowCreation ? renderCreateNew : undefined}
        values={searchResults}
        selectedValues={items}
        onSearch={onSearch}
        displaySelected
        hasMore={hasMore}
        loadMore={loadMore}
      />
    </AnchoredSheet>
  );
}

function EntityPickerItem({
  entity,
  workflowTemplate,
  secondaryText,
}: {
  entity: RelatedEntity;
  workflowTemplate?: TitledWorkflowTemplate;
  secondaryText?: string;
}) {
  return (
    <View style={{ paddingLeft: grid(1) }}>
      <Text size="body1">{getEntityTitle(entity, workflowTemplate) ?? ''}</Text>
      {!isNil(secondaryText) && (
        <Text size="body2" inactive numberOfLines={1}>
          {secondaryText}
        </Text>
      )}
    </View>
  );
}
